import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Highcharts } from 'core/components';
import QueryModel from 'app/stores/ude/QueryModel';
import styled, { useTheme } from 'styled-components';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { debounce, merge } from 'lodash';
import $syn from 'app/stores/synthetics/$syn';
import AgentModel from 'app/stores/synthetics/AgentModel';
import TestModel from 'app/stores/synthetics/TestModel';
import { DimensionFormatter, generateHighchartsOptions } from './util/highcharts';

const ChartContainer = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  position: relative;
`;

interface Props {
  queryModel: QueryModel;
  measurement: Measurement;
  /** Optional callback to receive the Highcharts chart instance (for cross-chart sync). */
  onChartRef?: (chart: Highcharts.Chart | null) => void;
  /** Arbitrary Highcharts options deep-merged into the generated chart config. */
  chartOverrides?: Highcharts.Options;
}

/** Dimensions that should render agent names instead of IDs */
const AGENT_DIMENSIONS = ['src_agent', 'dst_agent'];

/** Dimensions that should render test names instead of IDs */
const TEST_DIMENSIONS = ['test_id'];

function HighchartsDataView({ queryModel, measurement, onChartRef, chartOverrides }: Props) {
  const theme = useTheme();
  const chartRef = useRef<Highcharts.Chart | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState<number | null>(400);

  // Create a dimension formatter that looks up agent/test names from the $syn store
  const dimensionFormatter: DimensionFormatter = useCallback((dimensionName: string, value: string): string => {
    if (AGENT_DIMENSIONS.includes(dimensionName) && value) {
      const { agents } = $syn;
      const agent = agents?.get(value) as AgentModel | undefined;
      return agent?.label || value;
    }
    if (TEST_DIMENSIONS.includes(dimensionName) && value) {
      const { tests } = $syn;
      const test = tests?.get(value) as TestModel | undefined;
      return (test?.get('display_name') || test?.get('name') || value) as string;
    }
    return value;
  }, []);

  const { chartOptions, seriesRowIds } = useMemo(() => {
    const result = generateHighchartsOptions(queryModel, measurement, theme, dimensionFormatter);
    const height = result.options.chart?.height ?? containerHeight ?? undefined;
    const baseOptions = {
      ...result.options,
      chart: {
        ...result.options.chart,
        height
      }
    };
    return {
      chartOptions: chartOverrides ? merge({}, baseOptions, chartOverrides) : baseOptions,
      seriesRowIds: result.seriesRowIds
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryModel,
    queryModel.effectiveVizLimit,
    queryModel.results,
    queryModel.results.timestamps,
    queryModel.settings,
    theme,
    measurement,
    containerHeight,
    dimensionFormatter,
    chartOverrides
  ]);

  // Publish charted row IDs to QueryModel so the legend knows which rows have chart series.
  // useLayoutEffect runs synchronously before the browser paints, preventing the 1-frame flash
  // where all rows appear charted (which happened with useEffect's deferred timing).
  useLayoutEffect(() => {
    queryModel.setChartedRowIds(seriesRowIds);
  }, [seriesRowIds, queryModel]);

  // Sync series visibility: observe seriesVisibility map → call series.setVisible()
  // Uses queryModel.chartedRowIds (@observable.ref) in the tracking function so MobX
  // properly re-tracks when the chart rebuilds with new row IDs after query transitions.
  useEffect(() => {
    const disposer = reaction(
      () => queryModel.chartedRowIds.map((id) => queryModel.isSeriesVisible(id)),
      (visibilityList) => {
        const chart = chartRef.current;
        if (!chart) {
          return;
        }

        // For aggregate charts (single series, multiple points): toggle point visibility
        const isAggregate = chart.series.length === 1 && visibilityList.length > 1;

        if (isAggregate) {
          const series = chart.series[0];
          visibilityList.forEach((visible, idx) => {
            const point = series?.data?.[idx];
            if (point && point.visible !== visible) {
              // Highcharts types don't include `visible` in PointOptionsObject but it works at runtime for pie/bar
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              point.update({ visible } as any, false);
            }
          });
        } else {
          visibilityList.forEach((visible, idx) => {
            const series = chart.series[idx];
            if (series && series.visible !== visible) {
              series.setVisible(visible, false);
            }
          });
        }
        chart.redraw();
      },
      { fireImmediately: true }
    );
    return disposer;
  }, [queryModel]);

  // Sync hovered series: observe hoveredSeriesId → call series.setState()
  // Tracks both hoveredSeriesId and chartedRowIds so the reaction re-runs with
  // fresh row→index mapping after query transitions.
  useEffect(() => {
    let prevHoveredId: string | null = null;
    const disposer = reaction(
      () => ({ hoveredId: queryModel.hoveredSeriesId, rowIds: queryModel.chartedRowIds }),
      ({ hoveredId, rowIds }) => {
        const chart = chartRef.current;
        if (!chart) {
          return;
        }

        // For aggregate charts (single series, multiple points): highlight points instead of series
        const isAggregate = chart.series.length === 1 && rowIds.length > 1;

        // Unhighlight previous
        if (prevHoveredId) {
          const prevIdx = rowIds.indexOf(prevHoveredId);
          if (prevIdx >= 0) {
            if (isAggregate) {
              const point = chart.series[0]?.data?.[prevIdx];
              if (point) {
                point.setState();
              }
            } else if (chart.series[prevIdx]) {
              chart.series[prevIdx].setState();
            }
          }
          chart.renderer.boxWrapper.removeClass('highcharts-legend-series-active');
        }

        // Highlight current
        if (hoveredId) {
          const idx = rowIds.indexOf(hoveredId);
          if (idx >= 0) {
            if (isAggregate) {
              const point = chart.series[0]?.data?.[idx];
              if (point) {
                point.setState('hover');
              }
            } else if (chart.series[idx]) {
              chart.series[idx].setState('hover');
            }
            chart.renderer.boxWrapper.addClass('highcharts-legend-series-active');
          }
        }

        prevHoveredId = hoveredId;
      },
      { fireImmediately: false }
    );
    return disposer;
  }, [queryModel]);

  const chartCallback = useCallback(
    (chart: Highcharts.Chart) => {
      // If there is an existing chart instance, notify consumer that it is being replaced
      if (chartRef.current && chartRef.current !== chart) {
        onChartRef?.(null);
      }

      chartRef.current = chart;
      onChartRef?.(chart);
    },
    [onChartRef]
  );

  // Notify consumer when the chart is destroyed on unmount
  useEffect(
    () => () => {
      onChartRef?.(null);
    },
    [onChartRef]
  );

  // Use ResizeObserver to track container size and update chart height
  useEffect(() => {
    const container = containerRef.current;
    // we can't resize without a container, and we shouldn't resize charts that have a fixed size
    // note that we can't rely on `chartOptions.chart.height` to tell us that though, due to how `containerHeight` works.
    if (!container || queryModel.settings.chart.type === 'timelineView') {
      return undefined;
    }

    const handleResize = debounce((entries: ResizeObserverEntry[]) => {
      const entry = entries[0];
      if (entry) {
        const newHeight = entry.contentRect.height;
        setContainerHeight(newHeight);
        // Also resize the chart if it exists and is fully initialized
        if (chartRef.current?.options) {
          chartRef.current.setSize(undefined, newHeight, false);
        }
      }
    }, 100);

    const resizeObserver = new ResizeObserver(handleResize);

    resizeObserver.observe(container);

    return () => {
      handleResize.cancel();
      resizeObserver.disconnect();
    };
  }, [queryModel.settings.chart.type]);

  // the key forces the chart to reinitialize when the chart type changes
  return (
    <ChartContainer ref={containerRef}>
      {(chartOptions.chart?.height || containerHeight !== null) && (
        <Highcharts
          key={queryModel.settings.chart.type}
          styledMode={false}
          callback={chartCallback}
          options={chartOptions}
          allowChartUpdate
        />
      )}
    </ChartContainer>
  );
}

export default observer(HighchartsDataView);
